'use client';

import React, { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/domains/auth/context/auth-context';
import { Result, Button } from 'antd';
import { AuthPendingScreen } from '@/shared/components/feedback/AuthPendingScreen';

interface MfaRouteGuardProps {
  children: React.ReactNode;
}

/**
 * MFA gate. While auth is unresolved → AuthPendingScreen.
 * AppLoadingScreen is not used here (app content is not loading yet).
 */
export default function MfaRouteGuard({ children }: MfaRouteGuardProps) {
  const { isAuthenticated, requiresMFA, isInitialized, isLoading } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (isInitialized && !isLoading) {
      if (!isAuthenticated) {
        router.push('/auth/login');
        return;
      }

      if (requiresMFA) {
        router.push('/auth/mfa/provisional-setup');
        return;
      }
    }
  }, [isAuthenticated, requiresMFA, isInitialized, isLoading, router]);

  // Auth tier: wait for session before deciding MFA / access
  if (!isInitialized || isLoading) {
    return <AuthPendingScreen phase="restore" />;
  }

  if (!isAuthenticated) {
    return (
      <Result
        status="403"
        title="Access Denied"
        subTitle="You must be authenticated to access this page."
        extra={
          <Button type="primary" onClick={() => router.push('/auth/login')}>
            Go to Login
          </Button>
        }
      />
    );
  }

  if (requiresMFA) {
    return (
      <Result
        status="403"
        title="MFA Required"
        subTitle="Multi-factor authentication must be completed before accessing this page."
        extra={
          <Button type="primary" onClick={() => router.push('/auth/mfa/provisional-setup')}>
            Complete MFA Setup
          </Button>
        }
      />
    );
  }

  return <>{children}</>;
}
